// Pure cart-session decisions shared by logout and the guest 401 recovery.
// No Vue, axios, or Mango imports — node tests load this file directly.

export const CART_STORAGE_KEYS = ['cart']

let cartEpoch = 0

export function currentCartEpoch() {
	return cartEpoch
}

/** Drop in-flight saves from the cart module that was bound before logout. */
export function bumpCartEpoch() {
	cartEpoch += 1
	return cartEpoch
}

export function isStaleCartEpoch(epoch) {
	return epoch !== cartEpoch
}

export function emptyGuestCart() {
	return {
		cartItems: [],
		certificates: [],
		coupon: { code: '' },
		couponDiscount: 0,
		couponType: false,
		certificate: null,
		certificateDiscount: 0,
	}
}

/**
 * Same choice initCart makes. An empty local cart must fall through to the
 * member's latest open cart so login still restores it after logout cleared storage.
 */
export function selectInitialCart({ localCart, dbCart, localHasValidItems, dbHasValidItems }) {
	if (localHasValidItems && dbHasValidItems) {
		const localUpdated = localCart?.updated ? new Date(localCart.updated).getTime() : 0
		const dbUpdated = dbCart?.updated ? new Date(dbCart.updated).getTime() : 0
		return dbUpdated > localUpdated ? dbCart : localCart
	}
	if (localHasValidItems) return localCart
	if (dbHasValidItems && dbCart?.id) return dbCart
	return emptyGuestCart()
}

export function isAuthDeniedError(error) {
	const status = Number(error?.response?.status ?? error?.status)
	if (status === 401 || status === 403) return true
	const data = error?.response?.data ?? error?.data ?? error
	if (data?.authorized === false) return true
	if (data?.response?.authorized === false) return true
	return false
}

/**
 * A shopper with no stored user and no stored token is a guest, even when an
 * initCart closure still holds the member object from before logout.
 */
export function isGuestShopper(user, storage) {
	let userId = ''
	let token = ''
	try {
		const store = storage || (typeof window !== 'undefined' ? window.localStorage : null)
		userId = store?.getItem?.('user') || ''
		token = store?.getItem?.('token') || ''
	} catch {
		userId = ''
		token = ''
	}
	if (userId || token) return false
	return true
}

function lineKey(line) {
	const item = line?.item?.id || line?.item || ''
	const variant = line?.variant?.id || line?.variant || ''
	return `${item}:${variant}`
}

function addedLines(originalItems, currentItems) {
	const originalQty = new Map()
	for (const line of originalItems || []) {
		const key = lineKey(line)
		originalQty.set(key, (originalQty.get(key) || 0) + (Number(line?.quantity) || 0))
	}
	const added = []
	for (const line of currentItems || []) {
		const prev = originalQty.get(lineKey(line)) || 0
		const qty = Number(line?.quantity) || 0
		if (qty > prev) added.push({ ...line, quantity: qty - prev })
	}
	return added
}

function certKey(cert) {
	return `${cert?.code || cert?.id || ''}:${cert?.amount || ''}`
}

function addedCertificates(originalCerts, currentCerts) {
	const seen = new Set((originalCerts || []).map(certKey))
	return (currentCerts || []).filter((cert) => cert && !seen.has(certKey(cert)))
}

/**
 * Fresh empty guest cart, plus only the lines the shopper just added.
 * Member lines that were already in the cart stay out.
 */
export function guestCartAfterStaleAuth({ cart, originalCart } = {}) {
	const next = emptyGuestCart()
	next.cartItems = addedLines(originalCart?.cartItems, cart?.cartItems)
	next.certificates = addedCertificates(originalCart?.certificates, cart?.certificates)
	const nextCode = cart?.coupon?.code || ''
	const prevCode = originalCart?.coupon?.code || ''
	if (nextCode && nextCode !== prevCode) {
		next.coupon = { ...cart.coupon }
		next.couponDiscount = cart?.couponDiscount || 0
		next.couponType = cart?.couponType || false
	}
	return next
}

export function planStaleCartRecovery({ user, storage, error, cart, originalCart, attempt = 0 } = {}) {
	if (attempt >= 1) return { recover: false, attempt }
	if (!isGuestShopper(user, storage)) return { recover: false, attempt }
	if (!isAuthDeniedError(error)) return { recover: false, attempt }
	if (!cart?.id && !cart?.bcId) return { recover: false, attempt }
	return {
		recover: true,
		retry: true,
		attempt: attempt + 1,
		cart: guestCartAfterStaleAuth({ cart, originalCart }),
		dropStorageKeys: CART_STORAGE_KEYS,
	}
}
